import { useMemo, useState } from "react";
import { hotTopics } from "@/mocks/hotTopics";
import SectionHeading from "./SectionHeading";

const boards = [
  { key: "today", label: "今日热度榜" },
  { key: "week", label: "本周热度榜" },
] as const;

const rankStyle = (rank: number) => {
  if (rank === 1) return "bg-primary-500 text-background-50";
  if (rank === 2) return "bg-secondary-500 text-background-950";
  if (rank === 3) return "bg-accent-500 text-background-950";
  return "bg-background-200 text-foreground-700";
};

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line text-primary-600",
  down: "ri-arrow-down-line text-accent-700",
  flat: "ri-subtract-line text-foreground-400",
};

const trendText: Record<string, string> = {
  up: "升温",
  down: "回落",
  flat: "持平",
};

function formatHeat(value: number) {
  return `${(value / 10000).toFixed(1)} 万`;
}

export default function HotRanking() {
  const [board, setBoard] = useState<(typeof boards)[number]["key"]>("today");

  const list = useMemo(() => hotTopics.filter((item) => item.board === board).sort((a, b) => a.rank - b.rank), [board]);

  return (
    <section id="hot" className="scroll-mt-24 w-full bg-background-100">
      <div className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <SectionHeading
            eyebrow="热门瓜 · 热度排行"
            title="大家此刻都在讨论什么"
            desc="热度值来自时间线内相关快讯的讨论量与互动量汇总，按小时刷新。榜单帮你在信息很多的时候，先抓住最值得看的那几条。"
            tone="secondary"
          />

          <div className="shrink-0 inline-flex p-1 rounded-full bg-background-50 border border-background-200">
            {boards.map((item) => {
              const active = item.key === board;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setBoard(item.key)}
                  aria-pressed={active}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    active ? "bg-secondary-500 text-background-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <ol className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
          {list.map((item) => (
            <li key={item.id}>
              <a
                href="#live"
                className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-secondary-400 cursor-pointer"
              >
                <span
                  className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center font-heading text-lg ${rankStyle(item.rank)}`}
                >
                  {item.rank}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                      {item.category}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                      <i className={trendIcon[item.trend]} aria-hidden="true"></i>
                      {trendText[item.trend]}
                    </span>
                  </div>
                  <h3 className="mt-2 text-sm md:text-base font-medium text-foreground-950 leading-snug group-hover:text-secondary-800 transition-colors">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500 leading-relaxed line-clamp-2">{item.note}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-heading text-lg text-primary-600 tabular-nums leading-none">{formatHeat(item.heat)}</p>
                  <p className="mt-1 text-[11px] text-foreground-500">热度</p>
                </div>
              </a>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}